import { shouldPrerenderPublicRoute, } from "../core/index.js";
function normalizeLastModified(value) {
    return value instanceof Date ? value.toISOString() : value;
}
function assertCount(source, count) {
    if (!Number.isSafeInteger(count) || count < 0)
        throw new Error(`Public resource source '${source}' returned an invalid count.`);
}
function assertPriority(source, priority) {
    if (!Number.isFinite(priority) || priority < 0 || priority > 1)
        throw new Error(`Public resource source '${source}' returned invalid priority '${priority}'.`);
}
function getResourceId(routeKey, path, localeCode, sourceId) {
    return [routeKey, sourceId, localeCode ?? "x-default", path]
        .filter(Boolean)
        .join(":");
}
export function definePublicResourceSource(source) {
    return source;
}
export function definePublicResourceSources(sources) {
    return sources;
}
export function createPublicResourceCatalog(options) {
    const sources = options.sources ?? {};
    const pageSize = options.pageSize ?? 1_000;
    const collections = options.site.getResourceCollections();
    const sitemapByPath = new Map(options.site.getSitemapEntries().map((entry) => [entry.path, entry]));
    if (!Number.isSafeInteger(pageSize) || pageSize < 1)
        throw new Error("Public resource pageSize must be a positive safe integer.");
    for (const collection of collections)
        if (!options.allowMissingSources && !sources[collection.source])
            throw new Error(`Missing public resource source '${collection.source}'.`);
    for (const source of Object.keys(sources))
        if (!collections.some((collection) => collection.source === source))
            throw new Error(`Public resource source '${source}' is not declared by the site manifest.`);
    async function* iterateStatic() {
        for (const path of options.site.getIndexableUrls()) {
            const match = options.site.matchRoute(path);
            if (!match)
                continue;
            const sitemap = sitemapByPath.get(path);
            const localeCode = options.site.getLocaleCodeFromPath(path);
            yield {
                id: getResourceId(match.route.key, path, localeCode),
                routeKey: match.route.key,
                path,
                localeCode,
                route: match.route,
                params: match.params,
                lastModified: sitemap?.lastmod,
                sitemap: sitemap
                    ? {
                        lastmod: sitemap.lastmod,
                        changefreq: sitemap.changefreq,
                        priority: sitemap.priority,
                    }
                    : false,
                prerender: shouldPrerenderPublicRoute(match.route),
                markdown: match.route.access === "public",
                llms: match.route.llms,
            };
        }
    }
    async function* iterateDynamic() {
        for (const collection of collections) {
            const source = sources[collection.source];
            if (!source)
                continue;
            const count = await source.count();
            assertCount(collection.source, count);
            const pageCount = Math.ceil(count / pageSize);
            for (let page = 1; page <= pageCount; page++) {
                const items = await source.getPage({ page, pageSize });
                for (const item of items) {
                    const logicalPath = source.getPath(item);
                    const match = options.site.matchRoute(logicalPath);
                    if (!match || match.route.key !== collection.key)
                        throw new Error(`Public resource source '${collection.source}' returned path '${logicalPath}' outside route '${collection.pathPattern}'.`);
                    const lastModified = normalizeLastModified(source.getLastModified(item));
                    const changefreq = source.getChangeFrequency?.(item) ?? collection.changefreq;
                    const priority = source.getPriority?.(item) ?? collection.priority;
                    const sourceId = source.getId?.(item);
                    const contentId = source.getContentId?.(item) ?? undefined;
                    const data = await source.getData?.(item);
                    if (priority !== undefined)
                        assertPriority(collection.source, priority);
                    for (const path of options.site.getIndexablePaths(logicalPath)) {
                        const localizedMatch = options.site.matchRoute(path);
                        if (!localizedMatch)
                            continue;
                        const localeCode = options.site.getLocaleCodeFromPath(path);
                        yield {
                            id: getResourceId(localizedMatch.route.key, path, localeCode, sourceId),
                            routeKey: localizedMatch.route.key,
                            path,
                            localeCode,
                            route: localizedMatch.route,
                            params: localizedMatch.params,
                            source: collection.source,
                            sourceId,
                            contentId,
                            data,
                            lastModified,
                            sitemap: changefreq !== undefined && priority !== undefined
                                ? {
                                    lastmod: lastModified,
                                    changefreq,
                                    priority,
                                }
                                : false,
                            prerender: shouldPrerenderPublicRoute(localizedMatch.route),
                            markdown: localizedMatch.route.access === "public",
                            llms: localizedMatch.route.llms,
                        };
                    }
                }
            }
        }
    }
    async function* iterate() {
        yield* iterateStatic();
        yield* iterateDynamic();
    }
    const list = async () => {
        const resources = [];
        for await (const resource of iterate())
            resources.push(resource);
        return resources;
    };
    return {
        iterate,
        list,
    };
}
//# sourceMappingURL=index.js.map